// src/field.ts
function assertCompatibleDisplayNameAlias(builderName, config) {
  if (config?.label !== void 0 && config.displayName !== void 0) {
    throw new Error(
      `field.${builderName}(): pass either "label" or "displayName", not both. They are aliases for the same metadata.`
    );
  }
}
function isFieldConfigArgument(value) {
  return typeof value === "object" && value !== null && !("_type" in value);
}
function compactConfig(config) {
  if (config === void 0) {
    return {};
  }
  const entries = Object.entries(config).filter(([, value]) => value !== void 0);
  return Object.fromEntries(entries);
}
function createFieldBuilders() {
  return {
    text: (name, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("text", config);
      return {
        _type: "field",
        _field: "text",
        name,
        ...compactConfig(config)
      };
    },
    number: (name, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("number", config);
      return {
        _type: "field",
        _field: "number",
        name,
        ...compactConfig(config)
      };
    },
    boolean: (name, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("boolean", config);
      return {
        _type: "field",
        _field: "boolean",
        name,
        ...compactConfig(config)
      };
    },
    enum: (name, options, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("enum", config);
      if (options.length > 0) {
        const first = options[0];
        const firstIsObject = typeof first === "object";
        for (const opt of options) {
          const optIsObject = typeof opt === "object";
          if (optIsObject !== firstIsObject) {
            throw new Error(
              `field.enum("${name}"): options must be all strings or all objects with {id, label}, not mixed. Received mixed types in options array.`
            );
          }
        }
        if (firstIsObject) {
          for (const opt of options) {
            const obj = opt;
            if (typeof obj.id !== "string" || typeof obj.label !== "string") {
              throw new Error(
                `field.enum("${name}"): object options must have string "id" and "label" properties. Received: ${JSON.stringify(opt)}`
              );
            }
          }
        }
      }
      return {
        _type: "field",
        _field: "enum",
        name,
        options,
        ...compactConfig(config)
      };
    },
    dynamicEnum: (name, source, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("dynamicEnum", config);
      return {
        _type: "field",
        _field: "dynamic_enum",
        name,
        source,
        ...compactConfig(config)
      };
    },
    dynamicSchema: (name, schemaSource, ...args) => {
      const [config] = args;
      assertCompatibleDisplayNameAlias("dynamicSchema", config);
      return {
        _type: "field",
        _field: "dynamic_schema",
        name,
        schemaSource,
        ...compactConfig(config)
      };
    },
    array: (name, ...args) => {
      const [firstArg, ...restArgs] = args;
      const config = isFieldConfigArgument(firstArg) ? firstArg : void 0;
      const items = config === void 0 ? args : restArgs;
      assertCompatibleDisplayNameAlias("array", config);
      return {
        _type: "field",
        _field: "array",
        name,
        items,
        ...compactConfig(config)
      };
    },
    arrayWithConfig: (name, config, ...items) => {
      assertCompatibleDisplayNameAlias("arrayWithConfig", config);
      return {
        _type: "field",
        _field: "array",
        name,
        items,
        ...compactConfig(config)
      };
    },
    object: (name, ...args) => {
      const [firstArg, ...restArgs] = args;
      const config = isFieldConfigArgument(firstArg) ? firstArg : void 0;
      const properties = config === void 0 ? args : restArgs;
      assertCompatibleDisplayNameAlias("object", config);
      return {
        _type: "field",
        _field: "object",
        name,
        properties,
        ...compactConfig(config)
      };
    },
    objectWithConfig: (name, config, ...properties) => {
      assertCompatibleDisplayNameAlias("objectWithConfig", config);
      return {
        _type: "field",
        _field: "object",
        name,
        properties,
        ...compactConfig(config)
      };
    }
  };
}
var field = createFieldBuilders();

// src/factory.ts
import { _attachFormSpecMetadataPolicy } from "@formspec/core/internals";

// src/validation.ts
function collectFieldNames(elements, path = "") {
  const fieldNames = /* @__PURE__ */ new Map();
  function visit(elements2, currentPath) {
    for (const element of elements2) {
      switch (element._type) {
        case "field": {
          const field2 = element;
          const fieldPath = currentPath ? `${currentPath}.${field2.name}` : field2.name;
          const existing = fieldNames.get(field2.name);
          if (existing !== void 0) {
            existing.count++;
            existing.paths.push(fieldPath);
          } else {
            fieldNames.set(field2.name, { count: 1, paths: [fieldPath] });
          }
          if (field2._field === "array") {
            const arrayField = field2;
            visit(arrayField.items, `${fieldPath}[]`);
          } else if (field2._field === "object") {
            const objectField = field2;
            visit(objectField.properties, fieldPath);
          }
          break;
        }
        case "group": {
          const group2 = element;
          const groupPath = currentPath ? `${currentPath}.[${group2.label}]` : `[${group2.label}]`;
          visit(group2.elements, groupPath);
          break;
        }
        case "conditional": {
          const conditional = element;
          const conditionalPath = currentPath ? `${currentPath}.when(${conditional.field})` : `when(${conditional.field})`;
          visit(conditional.elements, conditionalPath);
          break;
        }
      }
    }
  }
  visit(elements, path);
  return fieldNames;
}
function collectConditionalReferences(elements, path = "") {
  const references = [];
  function visit(elements2, currentPath) {
    for (const element of elements2) {
      switch (element._type) {
        case "field": {
          const field2 = element;
          const fieldPath = currentPath ? `${currentPath}.${field2.name}` : field2.name;
          if (field2._field === "array") {
            const arrayField = field2;
            visit(arrayField.items, `${fieldPath}[]`);
          } else if (field2._field === "object") {
            const objectField = field2;
            visit(objectField.properties, fieldPath);
          }
          break;
        }
        case "group": {
          const group2 = element;
          const groupPath = currentPath ? `${currentPath}.[${group2.label}]` : `[${group2.label}]`;
          visit(group2.elements, groupPath);
          break;
        }
        case "conditional": {
          const conditional = element;
          const conditionalPath = currentPath ? `${currentPath}.when(${conditional.field})` : `when(${conditional.field})`;
          references.push({
            fieldName: conditional.field,
            path: conditionalPath
          });
          visit(conditional.elements, conditionalPath);
          break;
        }
      }
    }
  }
  visit(elements, path);
  return references;
}
function validateForm(elements) {
  const issues = [];
  const fieldNames = collectFieldNames(elements);
  for (const [name, info] of fieldNames) {
    if (info.count > 1 && info.paths[0] !== void 0) {
      issues.push({
        severity: "error",
        message: `Duplicate field name "${name}" found ${String(info.count)} times at: ${info.paths.join(", ")}`,
        path: info.paths[0]
      });
    }
  }
  const references = collectConditionalReferences(elements);
  for (const ref of references) {
    if (!fieldNames.has(ref.fieldName)) {
      issues.push({
        severity: "error",
        message: `Conditional references non-existent field "${ref.fieldName}"`,
        path: ref.path
      });
    }
  }
  return {
    valid: issues.every((issue) => issue.severity !== "error"),
    issues
  };
}
function logValidationIssues(result, formName) {
  if (result.issues.length === 0) {
    return;
  }
  const prefix = formName ? `FormSpec "${formName}"` : "FormSpec";
  for (const issue of result.issues) {
    const location = issue.path ? ` at ${issue.path}` : "";
    const message = `${prefix}: ${issue.message}${location}`;
    if (issue.severity === "error") {
      console.error(message);
    } else {
      console.warn(message);
    }
  }
}

// src/structure.ts
function group(label, ...elements) {
  return { _type: "group", label, elements };
}
function when(predicate, ...elements) {
  return {
    _type: "conditional",
    field: predicate.field,
    value: predicate.value,
    elements
  };
}
function formspec(...elements) {
  return { elements };
}
function formspecWithValidation(options, ...elements) {
  if (options.validate) {
    const result = validateForm(elements);
    if (options.validate === "throw" && !result.valid) {
      const errors = result.issues.filter((i) => i.severity === "error").map((i) => i.message).join("; ");
      throw new Error(`Form validation failed: ${errors}`);
    }
    if (options.validate === true || options.validate === "warn") {
      logValidationIssues(result, options.name);
    }
  }
  return { elements };
}

// src/predicate.ts
function is(field2, value) {
  return {
    _predicate: "equals",
    field: field2,
    value
  };
}

// src/factory.ts
function applyMetadataPolicy(form, metadataPolicy) {
  return _attachFormSpecMetadataPolicy(form, metadataPolicy);
}
function scopeElement(element) {
  return element;
}
function createFormSpecFactory(options) {
  const metadataPolicy = options?.metadata;
  return {
    field: createFieldBuilders(),
    formspec: (...elements) => applyMetadataPolicy(formspec(...elements), metadataPolicy),
    formspecWithValidation: (validationOptions, ...elements) => applyMetadataPolicy(formspecWithValidation(validationOptions, ...elements), metadataPolicy),
    group: (label, ...elements) => scopeElement(group(label, ...elements)),
    when: (predicate, ...elements) => scopeElement(when(predicate, ...elements)),
    is
  };
}
export {
  createFieldBuilders,
  createFormSpecFactory,
  field,
  formspec,
  formspecWithValidation,
  group,
  is,
  logValidationIssues,
  validateForm,
  when
};
//# sourceMappingURL=index.js.map